import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'

import { currentLocale, useI18n } from '@/app/i18n'
import { useAuthStore } from '@/app/stores/auth'
import type { Software, SoftwareCode } from '@/app/stores/session'
import { softwareColors } from '@/app/theme'

/** The switcher's order of the employer softwares (legacy `ac, hr, re, on, en`); any other code after them. */
export const SOFTWARE_ORDER: readonly SoftwareCode[] = ['ac', 'hr', 're', 'on', 'en']

/** The two-letter mark on a software's tile ("Re", "Hr"), as the switcher and the setup wizard draw it. */
export function softwareMark(code: SoftwareCode): string {
  return code.charAt(0).toUpperCase() + code.slice(1)
}

const POSITION_COLOURS = [softwareColors.ac, softwareColors.hr, softwareColors.re, softwareColors.on, softwareColors.en]

/** A software's own colour; `ca` / `em` (a Freelancer's apps) take the colour of their place in the list. */
export function softwareColor(code: SoftwareCode, index = 0): string {
  return code in softwareColors
    ? softwareColors[code as keyof typeof softwareColors]
    : (POSITION_COLOURS[index] ?? softwareColors.re)
}

/** One software a record can be scoped to: the employer apps of the session (ids 1–5, `Soft1`–`Soft5`). */
export interface ScopeOption {
  id: number
  code: SoftwareCode
  mark: string
  title: string
  tagline: string
  color: string
}

const EMPLOYER: readonly SoftwareCode[] = ['re', 'on', 'hr', 'en', 'ac']

/** The softwares a form was given instead of the session's (DocumentForm's `softwares`). */
export type ScopeSoftwares = readonly Pick<Software, 'id' | 'short' | 'title'>[] | null | undefined

/**
 * The user's employer softwares (or `source`, when given) in the switcher's order, with their names and taglines in
 * the current language.
 */
export function useScopeOptions(source?: MaybeRefOrGetter<ScopeSoftwares>): ComputedRef<ScopeOption[]> {
  const { t } = useI18n()
  const auth = useAuthStore()
  const titles: Record<string, () => string> = {
    re: () => t('common.nav.recruiting'),
    on: () => t('common.nav.onboarding'),
    hr: () => t('common.nav.humanResources'),
    en: () => t('common.nav.engagement'),
    ac: () => t('common.nav.accounting'),
  }
  const taglines: Record<string, () => string> = {
    re: () => t('shell.nav.software.re'),
    on: () => t('shell.nav.software.on'),
    hr: () => t('shell.nav.software.hr'),
    en: () => t('shell.nav.software.en'),
    ac: () => t('shell.nav.software.ac'),
  }
  return computed(() =>
    [...(toValue(source) ?? auth.session?.softwares ?? [])]
      .filter((software) => EMPLOYER.includes(software.short))
      .sort((a, b) => SOFTWARE_ORDER.indexOf(a.short) - SOFTWARE_ORDER.indexOf(b.short))
      .map((software) => ({
        id: software.id,
        code: software.short,
        mark: softwareMark(software.short),
        title: titles[software.short]?.() ?? software.title,
        tagline: taglines[software.short]?.() ?? '',
        color: softwareColor(software.short),
      })),
  )
}

/**
 * What a new record starts with: the software the user creates it from (owner, 2026-11-02), when it is one of the
 * options; else every module (`[]`).
 */
export function defaultScope(softwareId: number | null, options: readonly Pick<ScopeOption, 'id'>[]): number[] {
  return softwareId !== null && options.some((option) => option.id === softwareId) ? [softwareId] : []
}

/** A task's or a document's flags `Soft1`–`Soft5` as software ids (none set = every module, `[]`). */
export function scopeFromFlags(record: Partial<Record<`Soft${1 | 2 | 3 | 4 | 5}`, number | null>>): number[] {
  return ([1, 2, 3, 4, 5] as const).filter((id) => record[`Soft${id}`] === 1)
}

/** The ids a record's answer carries (`Softwares`, notes and meetings): numbers 1–5, sorted; anything else `[]`. */
export function scopeFromList(value: unknown): number[] {
  if (!Array.isArray(value)) return []
  return [...new Set(value.map(Number).filter((id) => Number.isInteger(id) && id >= 1 && id <= 5))].sort()
}

/** What a save sends as `softwares` / `assigned_softs`: the ids as a comma list, `''` for every module. */
export function scopeParam(ids: readonly (number | string)[]): string {
  return ids.map(String).join(',')
}

/** The options a scope picks, in the options' order; every option for an empty scope. */
export function scopedOptions(ids: readonly number[], options: readonly ScopeOption[]): ScopeOption[] {
  return ids.length ? options.filter((option) => ids.includes(option.id)) : [...options]
}

const INTL_LOCALES: Record<string, string> = { cz: 'cs', gr: 'el' }

/**
 * "Recruiting and Human resources" in the current language (`Intl.ListFormat`); `short`: "Recruiting, Human
 * resources", for a pill.
 */
export function joinNames(names: readonly string[], style: 'long' | 'short' = 'long'): string {
  const locale = currentLocale()
  try {
    return new Intl.ListFormat(INTL_LOCALES[locale] ?? locale, {
      style,
      type: style === 'short' ? 'unit' : 'conjunction',
    }).format(names)
  } catch {
    return names.join(', ')
  }
}
